'use client';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { neutral7, primary } from '../../colors';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import Button from '../Button';
import IqviaLogo from '../IqviaLogo/IqviaLogo';
import IqviaLogoDark from '../IqviaLogo/IqviaLogoDark';
import MenuItem from '../MenuItem';
import SelectButton from '../SelectButton';
import Tooltip from '../Tooltip';
import Typography from '../Typography';

export const styles = ({ idsTokens = idsTokensDefault }, { maxWidth = '100%' }) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'content-box',
    paddingTop: idsTokens.footer.default.spacing.topPadding,
    paddingBottom: idsTokens.footer.default.spacing.bottomPadding,
    paddingLeft: idsTokens.footer.default.spacing.sidePadding,
    paddingRight: idsTokens.footer.default.spacing.sidePadding,
    gap: `${idsTokens.footer.default.spacing.rowGap} ${idsTokens.footer.default.spacing.itemGap}`,
    color: idsTokens.footer.default.color.content,
    containerType: 'inline-size',
    containerName: 'footer',
    '& .copyrightContainer': {
      display: 'flex',
      justifyContent: 'flex-end',
      flexGrow: 1,
      textAlign: 'right',
      height: 32,
    },
  },
  wrapper: {
    width: maxWidth,
    display: 'flex',
    alignItems: 'center',
    '@container footer (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  button: {
    width: 'fit-content',
    height: 32,
    '@container footer (max-width: 600px)': {
      marginRight: 0,
      justifyContent: 'flex-start',
    },
  },
  leftContainer: {
    display: 'flex',
    color: primary,
    maxWidth: '100%',
    '@container footer (max-width: 600px)': {
      width: '100%',
      alignItems: 'left',
    },
  },
  leftItems: {
    display: 'flex',
    gap: 16,
    '@container footer (max-width: 900px)': {
      flexWrap: 'wrap',
    },
    '@container footer (max-width: 600px)': {
      flexDirection: 'column',
    },
  },
  rightContainer: {
    display: 'flex',
    color: neutral7,
    height: 32,
    alignItems: 'center',
    letterSpacing: '0.26px',
    lineHeight: '19.5px',
    textAlign: 'right',
    '@container footer (max-width: 600px)': {
      width: 170,
    },
  },
  logoContainer: {
    paddingTop: 4,
    paddingRight: 32,
    '@container footer (max-width: 900px)': {
      paddingBottom: 32,
      paddingRight: 0,
    },
    '& a:any-link:focus-visible': {
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.button.default.color.outline}`,
      borderRadius: idsTokens.button.success.radius.standard.primary,
      outlineOffset: '2px',
    },
  },
  toggleLogoOff: {
    display: 'none',
  },
  widthLogo: {
    '@container footer (max-width: 900px)': {
      width: '100%',
    },
  },
  leftRightContainers: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
    gap: 16,
    '@container footer (max-width: 900px)': {
      gap: 32,
    },
    '@container footer (max-width: 600px)': {
      gap: 0,
    },
  },
  darkMode: {
    background: 'linear-gradient(90deg, #012865 0%, #013D98 100%)',
    color: idsTokens.footer.defaultInverse.color.content,
    '& a:any-link:focus-visible': {
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.button.onColor.color.outline}`,
      outlineOffset: '2px',
    },
  },
  darkModeRightContainer: {
    color: idsTokens.footer.defaultInverse.color.content,
  },
  darkModeButton: {
    color: idsTokens.button.onColor.color.discreet.content.enabled,
    border: `1px solid ${idsTokens.button.onColor.color.discreet.border.enabled}`,
    backgroundColor: idsTokens.button.onColor.color.discreet.background.enabled,
    '&:hover': {
      color: idsTokens.button.onColor.color.discreet.content.hover,
      backgroundColor: idsTokens.button.onColor.color.discreet.background.hover,
      border: `1px solid ${idsTokens.button.onColor.color.discreet.border.hover}`,
    },
    '&:active': {
      color: idsTokens.button.onColor.color.discreet.content.pressed,
      backgroundColor: idsTokens.button.onColor.color.discreet.background.pressed,
      border: `1px solid ${idsTokens.button.onColor.color.discreet.border.pressed}`,
    },
    '&:focus-visible': {
      color: idsTokens.button.onColor.color.discreet.content.focus,
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.button.onColor.color.outline}`,
      outlineOffset: '2px',
    },
  },
});

const useStyles = makeStyles()(styles);

export const Footer = ({
  darkMode,
  className,
  buttonProps,
  children,
  languagePickerProps = {},
  maxWidth,
  hideLogo,
  forwardedRef: ref,
  ...rest
}) => {
  const { classes, cx } = useStyles({ maxWidth });

  const { menuItems, ...languagePickerRest } = languagePickerProps;

  return (
    <div className={cx(classes.root, darkMode && classes.darkMode, className)} {...rest} ref={ref}>
      <div className={classes.wrapper}>
        <div className={cx(hideLogo && classes.toggleLogoOff, classes.widthLogo)}>
          <Tooltip
            variant={darkMode ? 'light' : 'dark'}
            title="Open https://www.iqvia.com/"
            placement="top"
            enterDelay={500}
            leaveDelay={0}
          >
            <div className={cx(classes.logoContainer)}>
              <a href="https://www.iqvia.com/" target="_blank" rel="noreferrer">
                {darkMode ? (
                  <IqviaLogoDark alt="www.iqvia.com" />
                ) : (
                  <IqviaLogo alt="www.iqvia.com" />
                )}
              </a>
            </div>
          </Tooltip>
        </div>
        <div className={classes.leftRightContainers}>
          <div className={classes.leftContainer}>
            <div className={classes.leftItems}>
              {buttonProps &&
                buttonProps.map((props, i) => {
                  return (
                    <Tooltip
                      variant={darkMode ? 'light' : 'dark'}
                      title={props.href}
                      placement="top"
                      key={i}
                      enterDelay={500}
                      leaveDelay={0}
                    >
                      <Button
                        className={cx(classes.button, darkMode && classes.darkModeButton)}
                        size="small"
                        variant="text"
                        key={i}
                        {...props}
                      >
                        {props.label}
                      </Button>
                    </Tooltip>
                  );
                })}
              {menuItems && (
                <Tooltip
                  variant={darkMode ? 'light' : 'dark'}
                  title="Pick a language"
                  placement="top"
                  enterDelay={500}
                  leaveDelay={0}
                >
                  <div style={{ display: 'flex' }}>
                    <SelectButton
                      className={cx(classes.button, darkMode && classes.darkModeButton)}
                      size="small"
                      {...languagePickerRest}
                    >
                      {menuItems.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.text}
                        </MenuItem>
                      ))}
                    </SelectButton>
                  </div>
                </Tooltip>
              )}
              {children}
            </div>
          </div>

          <div className="copyrightContainer">
            <Typography
              variant="caption"
              className={cx(classes.rightContainer, darkMode && classes.darkModeRightContainer)}
            >
              {`Copyright \u00A9\u00A0${new Date().getFullYear()} IQVIA. All Rights Reserved.`}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

Footer.propTypes = {
  /** Array of props for the `Button`s. */
  buttonProps: PropTypes.arrayOf(
    PropTypes.shape({
      /** URL of the page the link goes to. */
      href: PropTypes.string,
      /** Link text. */
      label: PropTypes.node,
      /** Callback fired when the component is clicked. */
      onClick: PropTypes.func,
      /** Target attribute for link. */
      target: PropTypes.string,
    })
  ),
  /** Footer content. */
  children: PropTypes.node,
  /** If `true`, the component has an altered appearance, for display on a dark background. */
  darkMode: PropTypes.bool,
  /** If `true`, the logo will not be displayed. */
  hideLogo: PropTypes.bool,
  /**
   * Props applied to the `SelectButton` component, for changing the language
   * of the page.
   */
  languagePickerProps: PropTypes.shape({
    /** The default value. Use when the component is not controlled.
     * @ignore */
    defaultValue: PropTypes.any,
    /** The menu items to display in the dropdown. */
    menuItems: PropTypes.arrayOf(
      PropTypes.shape({
        /** Language label. */
        text: PropTypes.node,
        /** The value corresponding to the language. */
        value: PropTypes.any,
      })
    ),
    /** If `true`, selecting the previously selected value will not deselect it. */
    noDeselect: PropTypes.bool,
    /** Callback fired when the value changes. */
    onChange: PropTypes.func,
    /** The short hint displayed in the input before the user enters a value.
     * @ignore */
    placeholder: PropTypes.string,
    /** The value of component. */
    value: PropTypes.any,
  }),
  /** Maximum width of the footer. */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default withRef()(Footer);
